// Need to use the React-specific entry point to allow generating React hooks
import { getQueryStringFromObject } from "@/utils/helper";
import { createApi, fetchBaseQuery } from "@reduxjs/toolkit/query/react";
import { baseQueryWithDecryption } from "../baseQueryWithEncryption";

export const MemberApi = createApi({
  reducerPath: "MemberApi",
  baseQuery: baseQueryWithDecryption(import.meta.env.VITE_COMPANY_API_URL),
  tagTypes: ["users", "apiKeys"],
  endpoints: (builder) => ({
    //Company
    getCompanyMembers: builder.query({
      query: ({
        searchName,
        roleCode,
        roleName,
        deptName,
        status,
        addedBy,
        createdAtStart,
        createdAtEnd,
        page,
        size,
      }) => {
        const params: string[] = ['all=true', "isGetAdmin=true"];

        if (searchName !== undefined) {
          params.push(`searchName=${searchName.trim()}`);
        }
        if (roleCode !== undefined) {
          params.push(`roleCode=${roleCode}`);
        }
        if (roleName !== undefined) {
          params.push(`roleName=${roleName}`);
        }
        if (deptName !== undefined) {
          params.push(`deptName=${deptName}`);
        }
        if (status !== undefined) {
          params.push(`status=${status?.toUpperCase()}`);
        }
        if (addedBy !== undefined) {
          params.push(`addedBy=${addedBy}`);
        }
        if (createdAtStart && createdAtEnd) {
          params.push(`createdAtStart=${createdAtStart}`);
          params.push(`createdAtEnd=${createdAtEnd}`);
        }
        if (page !== undefined) {
          params.push(`page=${page}`);
        }
        if (size !== undefined) {
          params.push(`size=${size}`);
        }

        const queryString = params.length > 0 ? `?${params.join('&')}` : '';
        return `/user-mgmt/users${queryString}`;
      },
      providesTags: ["users"],
    }),

    getCompanyInvitedMembers: builder.query({
      query: ({
        roleCode,
        page,
        size,
      }: {
        roleCode?: any;
        page?: number;
        size?: number;
      }) => {
        const prefix = `/team-member/invites`;
        const queryStr = getQueryStringFromObject({ roleCode, page, size });
        return `${prefix}${queryStr && `?${queryStr}`}`;
      },

      providesTags: ["users"],
    }),

    updateLastTeam: builder.mutation({
      query: ({ ...body }) => ({
        url: `/user-mgmt/user/teams`,
        method: "PATCH",
        body: body,
      }),
    }),

    createUser: builder.mutation({
      query: (payload) => ({
        url: "/user-mgmt/create-user",
        method: "POST",
        body: payload,
      }),
      invalidatesTags: ["users"],
    }),
    updateUser: builder.mutation({
      query: ({ userId, body }) => ({
        url: `/user-mgmt/edit-user/${userId}`,
        method: "PATCH",
        body: body,
      }),
      invalidatesTags: ["users"],
    }),
    deleteUser: builder.mutation({
      query: ({ userId }) => ({
        url: `/user-mgmt/delete-user/${userId}`,
        method: "DELETE",
      }),
      invalidatesTags: ["users"],
    }),
    getUserDetailsByUserId: builder.query({
      query: ({ userId }) => ({
        url: `/user-mgmt/details-user/${userId}`,
        method: "GET",
      }),
      providesTags: ["users"],
    }),
    generateClientCreds: builder.mutation({
      query: () => ({
        url: `user-mgmt/external/create`,
        method: "GET",
      }),
    }),
    deleteClientCredentials: builder.mutation({
      query: () => ({
        url: `user-mgmt/external/delete`,
        method: "DELETE",
      }),
    }),
    getApiKeys: builder.query({
      query: () => ({
        url: `api-key`,
        method: "GET",
      }),
      providesTags: ["apiKeys"],
    }),

    generateApiKey: builder.mutation({
      query: (deptName) => ({
        url: `api-key/generate`,
        method: "POST",
        body: {
          deptName,
        },
      }),
      invalidatesTags: ["apiKeys"],
    }),
    deleteApiKey: builder.mutation({
      query: ({ apiKeyId }) => ({
        url: `api-key/${apiKeyId}`,
        method: "DELETE",
      }),
      invalidatesTags: ["apiKeys"],
    }),
  }),
});

export const {
  //teams
  useGetCompanyMembersQuery,
  useGetCompanyInvitedMembersQuery,
  useLazyGetCompanyMembersQuery,
  useUpdateLastTeamMutation,
  useCreateUserMutation,
  useUpdateUserMutation,
  useDeleteUserMutation,
  useLazyGetUserDetailsByUserIdQuery,
  useDeleteClientCredentialsMutation,
  useGenerateClientCredsMutation,
  useDeleteApiKeyMutation,
  useGenerateApiKeyMutation,
  useGetApiKeysQuery,
} = MemberApi;
